import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { useResourceStatusMonitor } from "./hooks/useResourceStatusMonitor";
import { useAutoLoad } from "../../../hooks/useAutoLoad";
import { useGlobalFilters } from "../../../stores/GlobalFilterContext/GlobalFilterContext";
import { useOcelMetadata } from "../../../stores/OcelContext";
import { makePostRequest } from "../../../services/apiClient";
import { formatAttributeValue } from "../../../features/visualization/GanttChart/ganttTransform";
import { formatBerlinDisplay, addDaysToDateOnly, formatDateOnly as formatDateOnlyUtc } from "../../../utils/berlinDate";
import BaseTile from "../../../components/Tile/BaseTile";
import TileContent, { TileStatusContent } from "../../../components/Tile/TileContent";
import TileSettingsModal from "../../../components/common/TileSettingsModal";
import MultiSelectDropdown from "../../../components/ui/MultiSelectDropdown";
import ResourceStatusMonitorSettings from "./ResourceStatusMonitorSettings";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "../../../components/ui/tooltip";
import { Popover, PopoverTrigger, PopoverContent } from "../../../components/ui/popover";
import ObjectAttributeTable, { TOOLTIP_OBJECT_LIMIT } from "../../../components/common/ObjectAttributeTable";
import { useObjectTableFetch } from "../../../hooks/useObjectTableFetch";

// Shared "info card" visual tokens — matches TooltipSurfaces.jsx's
// CARD_CLASS (kept local here since this file composes raw Tooltip+Popover
// primitives directly, not the HoverInfoCard/PinnedInfoCard wrappers, so
// both surfaces can share exactly one trigger element per slot — see
// ResourceTracker below).
// w-auto overrides Popover's own base w-72 (fixed 18rem) — the card must
// size to its content (SlotInfoBody alone, or SlotInfoBody + a variable-width
// object table), never a fixed width.
const TOOLTIP_CARD_CLASS =
  "p-0 overflow-hidden max-w-none w-auto bg-white text-gray-900 border border-[#e2e8f0] rounded-[10px] " +
  "shadow-[0_8px_24px_rgba(0,0,0,0.13),0_2px_6px_rgba(0,0,0,0.07)]";

/** Combine selectedDate ("YYYY-MM-DD") + a slot's bare "HH:MM" boundary into
 * a UTC ISO instant — slot_start/slot_end are UTC wall-clock times of that
 * date (see kpi/service.py's get_resource_status_monitor, which builds day
 * boundaries from the OCEL timestamp column's own tz, always UTC per
 * pm4py), so this must NOT be reinterpreted as Berlin local time or the
 * object query would scope a different window than the one the slot's own
 * downtime/available numbers describe. "24:00" (the last slot's end) maps
 * to the next calendar day's 00:00. */
function slotBoundaryToUtcIso(selectedDate, hhmm) {
  if (hhmm === "24:00") {
    const [y, m, d] = selectedDate.split("-").map(Number);
    const nextDay = addDaysToDateOnly(new Date(Date.UTC(y, m - 1, d)), 1);
    return `${formatDateOnlyUtc(nextDay)}T00:00:00+00:00`;
  }
  return `${selectedDate}T${hhmm}:00+00:00`;
}

/** Format a date string as "DD. Monat YYYY" (e.g., 06. Februar 2025). */
function formatDisplayDate(value) {
  const parsed = new Date(value + "T00:00:00");
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

const DateInput = React.memo(function DateInput({ value, onChange, disabled }) {
  const committed = useRef(value);
  const inputRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const isPointerRef = useRef(false);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  useEffect(() => {
    if (inputRef.current) inputRef.current.value = value;
    committed.current = value;
  }, [value]);

  const commit = useCallback(() => {
    const val = inputRef.current?.value;
    if (!val || val === committed.current) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(val) || isNaN(new Date(val + "T00:00:00").getTime())) return;
    committed.current = val;
    onChangeRef.current(val);
  }, []);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    const onPointerDown = () => { isPointerRef.current = true; };
    const onKeyDown = () => { isPointerRef.current = false; };
    const onNativeChange = () => {
      if (isPointerRef.current) {
        isPointerRef.current = false;
        commit();
      }
    };
    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('keydown', onKeyDown);
    el.addEventListener('change', onNativeChange);
    return () => {
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('keydown', onKeyDown);
      el.removeEventListener('change', onNativeChange);
    };
  }, [commit]);

  return (
    <input
      ref={inputRef}
      type="date"
      defaultValue={value}
      onKeyDown={(e) => { if (e.key === "Enter") commit(); }}
      onBlur={commit}
      disabled={disabled}
      className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
    />
  );
});

/** Resolve slot color based on status and downtime threshold (traffic-light). */
function getSlotColor(slot, redThresholdMinutes) {
  if (slot.status === "outside_shift") return "bg-gray-300";
  if (slot.status === "pause") return "bg-gray-300";
  if (slot.status === "available") return "bg-emerald-500";
  // downtime slot — apply threshold
  return slot.downtime_seconds / 60 >= redThresholdMinutes
    ? "bg-red-500"
    : "bg-orange-400";
}

const normalizeNonNegativeNumber = (value, fallback = 0) => {
  if (value === "") return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(0, parsed) : fallback;
};

const STATUS_LABEL = {
  available: "Available",
  downtime: "Downtime",
  outside_shift: "Outside Shift",
};

/** Build border-radius class for Tremor-style continuous tracker bar. */
function barRounding(idx, total) {
  if (total === 1) return "rounded";
  if (idx === 0) return "rounded-l";
  if (idx === total - 1) return "rounded-r";
  return "";
}

/** Base slot info block (time range header + downtime/available/mode body) —
 * shared verbatim between the plain hover tooltip and the extended,
 * click-triggered tooltip, so the extended one never drifts from what the
 * standard tooltip shows. Owns its own useTranslation call (rather than
 * relying on a hook declared in an enclosing scope) — see CLAUDE.md's
 * hook-scope verification trap, which previously bit this exact file. */
function SlotInfoBody({ slot, isOutside, multiResourceMode, downtimeRedThreshold }) {
  const { t } = useTranslation("tiles");
  const modeLabel =
    multiResourceMode === "simultaneous" || multiResourceMode === "concurrent"
      ? t("resourceStatus.tooltip.modeConcurrent")
      : t("resourceStatus.tooltip.modeAccumulated");

  return (
    <>
      <div className="px-2.5 py-1.5 border-b border-gray-100 bg-primary-50 rounded-t-md flex items-center gap-2">
        <span className={`size-2 shrink-0 rounded-full ${getSlotColor(slot, downtimeRedThreshold)}`} />
        <span className="text-xs font-medium text-primary-700">
          {slot.slot_start} – {slot.slot_end}
        </span>
      </div>
      {!isOutside && (
        <div className="px-2.5 py-1.5 space-y-1 text-xs leading-normal">
          <div className="flex items-center justify-between gap-4">
            <span className="text-gray-500">{t("resourceStatus.tooltip.effectiveDowntime")}</span>
            <span className="text-primary-600 font-medium">
              {(slot.downtime_seconds / 60).toFixed(1)} min
            </span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span className="text-gray-500">{t("resourceStatus.tooltip.availableTime")}</span>
            <span className="text-primary-600 font-medium">
              {(slot.available_seconds / 60).toFixed(1)} min
            </span>
          </div>
          <div className="flex items-center justify-between gap-4 pt-1 border-t border-gray-100">
            <span className="text-gray-500">{t("resourceStatus.tooltip.mode")}</span>
            <span className="text-gray-500">{modeLabel}</span>
          </div>
        </div>
      )}
    </>
  );
}

/** Single resource tracker row — Tremor Tracker pattern. */
const ResourceTracker = React.memo(function ResourceTracker({
  resource,
  slots,
  multiResourceMode,
  downtimeRedThreshold,
  showShiftOnly,
  shiftStart,
  shiftEnd,
  activeFile,
  resourceColumn,
  objectType,
  selectedDate,
  tooltipAttributes,
  formatCellValue,
}) {
  const [hoveredIdx, setHoveredIdx] = useState(null); // purely visual dimming of other slots — not tooltip state
  const [selected, setSelected] = useState(null); // { idx } — click-triggered extended (pinned) tooltip
  const visibleSlots = showShiftOnly
    ? slots.filter((s) => s.status !== "outside_shift")
    : slots;

  // Use one device-pixel width for every slot. Leave the rounding remainder
  // at the end of the row instead of making individual slots wider.
  const rowRef = useRef(null);
  const [rowMetrics, setRowMetrics] = useState({ width: 0, left: 0, dpr: 1 });

  useEffect(() => {
    const el = rowRef.current;
    if (!el) return;
    let resolutionQuery;
    const measure = () => {
      const { width, left } = el.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      setRowMetrics((previous) =>
        previous.width === width && previous.left === left && previous.dpr === dpr
          ? previous
          : { width, left, dpr },
      );
    };
    const watchResolution = () => {
      resolutionQuery?.removeEventListener("change", watchResolution);
      measure();
      resolutionQuery = window.matchMedia(`(resolution: ${window.devicePixelRatio || 1}dppx)`);
      resolutionQuery.addEventListener("change", watchResolution);
    };
    const observer = new ResizeObserver(measure);
    observer.observe(el, { box: "border-box" });
    window.addEventListener("resize", measure);
    watchResolution();
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
      resolutionQuery?.removeEventListener("change", watchResolution);
    };
  }, []);

  const slotLayout = useMemo(() => {
    const { width, left, dpr } = rowMetrics;
    const n = visibleSlots.length;
    if (n === 0 || width === 0) return { widthFor: () => undefined, gap: 2, offset: 0 };
    const gapPixels = Math.max(1, Math.round(2 * dpr));
    const offsetPixels = Math.ceil(left * dpr) - left * dpr;
    const availablePixels = Math.max(0, Math.floor(width * dpr - offsetPixels) - gapPixels * (n - 1));
    // Preserve the existing minimum size for very dense/narrow rows.
    const minPixels = Math.ceil(2 * dpr);
    // Every block is a whole number of device pixels, but the rounding
    // remainder is spread across the row (some blocks one pixel wider) so the
    // last block ends exactly at the row's right edge instead of short of it.
    const widthFor = (idx) => {
      const px = Math.floor(((idx + 1) * availablePixels) / n) - Math.floor((idx * availablePixels) / n);
      return Math.max(minPixels, px) / dpr;
    };
    return { widthFor, gap: gapPixels / dpr, offset: offsetPixels / dpr };
  }, [rowMetrics, visibleSlots.length]);

  // Click-triggered object breakdown — only wired up when tooltip columns
  // are configured; clicking a slot otherwise does nothing (no handler
  // attached at all), so the plain tooltip is the only thing that ever
  // shows. useObjectTableFetch gives single-flight-with-latest-wins +
  // loaded-key dedupe for free — a click that arrives while a request is in
  // flight replaces whatever was pending (always converges on the LAST
  // clicked slot, never queues a backlog).
  const wantsObjectBreakdown = Array.isArray(tooltipAttributes) && tooltipAttributes.length > 0 && !!objectType && !!activeFile;
  const {
    data: objData,
    loading: objLoading,
    error: objError,
    request: requestObjectBreakdown,
  } = useObjectTableFetch();

  const buildRequestKey = useCallback((slot) => {
    const windowStart = slotBoundaryToUtcIso(selectedDate, slot.slot_start);
    const windowEnd = slotBoundaryToUtcIso(selectedDate, slot.slot_end);
    return {
      key: [activeFile, objectType, resource, windowStart, windowEnd, tooltipAttributes.join(" ")].join("|"),
      windowStart,
      windowEnd,
    };
  }, [activeFile, objectType, resource, selectedDate, tooltipAttributes]);

  const fetchSlotBreakdown = useCallback((slot) => {
    const { key, windowStart, windowEnd } = buildRequestKey(slot);
    requestObjectBreakdown(key, () =>
      makePostRequest("/ocel/resource-object-breakdown", {
        file_type: activeFile,
        resource_column: resourceColumn,
        resource,
        object_type: objectType,
        start_date: windowStart,
        end_date: windowEnd,
        selected_attributes: tooltipAttributes,
        limit: TOOLTIP_OBJECT_LIMIT,
      }, {}, "Resource Object Breakdown"),
    );
  }, [buildRequestKey, requestObjectBreakdown, activeFile, resourceColumn, resource, objectType, tooltipAttributes]);

  const handleSlotClick = useCallback((slot, idx) => {
    if (!wantsObjectBreakdown) return;
    setSelected((prev) => {
      const isToggleOff = prev && prev.idx === idx;
      return isToggleOff ? null : { idx };
    });
    const isToggleOff = selected && selected.idx === idx;
    if (!isToggleOff) fetchSlotBreakdown(slot);
  }, [wantsObjectBreakdown, selected, fetchSlotBreakdown]);

  const objRows = objData?.rows ?? [];
  const objTotal = objData?.aggregations?.total_objects ?? 0;

  return (
    <div className="py-1.5">
      {/* Header: resource name */}
      <p
        className="text-sm font-medium text-gray-900 truncate mb-1"
        title={resource}
      >
        {resource}
      </p>

      {/* Tracker bar — Tremor-style: continuous strip, rounded only at ends.
          Each slot composes one Radix Tooltip (hover) and one Popover
          (click-pinned) on the SAME trigger element via nested asChild —
          Radix's documented pattern for combining primitives on one trigger.
          The Tooltip is forced closed while this slot is pinned so the two
          surfaces never visually stack. */}
      <div ref={rowRef} className="relative flex items-center w-full" style={{ gap: slotLayout.gap, paddingLeft: slotLayout.offset, boxSizing: "border-box" }} onMouseLeave={() => setHoveredIdx(null)}>
        {visibleSlots.map((slot, idx) => {
          const isOutside = slot.status === "outside_shift" || slot.status === "pause";
          const isSelected = selected?.idx === idx;
          const width = slotLayout.widthFor(idx);
          return (
            <Tooltip key={idx} open={isSelected ? false : undefined}>
              <Popover
                open={isSelected}
                onOpenChange={(nextOpen) => { if (!nextOpen) setSelected(null); }}
              >
                <TooltipTrigger asChild>
                  <PopoverTrigger asChild>
                    <div
                      className={`h-8 ${width ? "" : "flex-1"} ${barRounding(idx, visibleSlots.length)} ${getSlotColor(slot, downtimeRedThreshold)} transition-opacity ${
                        hoveredIdx !== null && hoveredIdx !== idx ? "opacity-50" : ""
                      } cursor-pointer`}
                      style={{ width, flex: width ? "0 0 auto" : "1 1 0%", minWidth: "2px" }}
                      onMouseEnter={() => setHoveredIdx(idx)}
                      onClick={() => handleSlotClick(slot, idx)}
                    />
                  </PopoverTrigger>
                </TooltipTrigger>
                <PopoverContent
                  side="top"
                  align="center"
                  onOpenAutoFocus={(e) => e.preventDefault()}
                  className={`${TOOLTIP_CARD_CLASS} min-w-[180px]`}
                >
                  <SlotInfoBody slot={slot} isOutside={isOutside} multiResourceMode={multiResourceMode} downtimeRedThreshold={downtimeRedThreshold} />
                  {!isOutside && (
                    <ObjectAttributeTable
                      attributes={tooltipAttributes}
                      rows={objRows}
                      totalCount={objTotal}
                      loading={objLoading}
                      error={objError}
                      formatCellValue={formatCellValue}
                    />
                  )}
                </PopoverContent>
              </Popover>
              <TooltipContent side="top" align="center" className={`${TOOLTIP_CARD_CLASS} min-w-[180px]`}>
                <SlotInfoBody slot={slot} isOutside={isOutside} multiResourceMode={multiResourceMode} downtimeRedThreshold={downtimeRedThreshold} />
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>

    </div>
  );
});

/** Legend — Tremor-style inline pills with small round dots. */
const StatusLegend = ({ redThresholdMinutes = 5, showShiftOnly = false }) => {
  const { t } = useTranslation('tiles');
  const items = [
    { color: "bg-emerald-500", label: t('status.available') },
    { color: "bg-orange-400", label: t('status.downtimeBelow', { n: redThresholdMinutes }) },
    { color: "bg-red-500", label: t('status.downtimeAbove', { n: redThresholdMinutes }) },
    ...(showShiftOnly
      ? [{ color: "bg-gray-300", label: t('status.pause') }]
      : [{ color: "bg-gray-300", label: t('status.outsideShift') }]),
  ];
  return (
    <div className="flex items-center gap-3 text-sm text-gray-500">
      {items.map(({ color, label }) => (
        <span
          key={label}
          className="inline-flex items-center gap-x-2 rounded bg-gray-100 px-2 py-0.5 text-xs"
        >
          <span className={`size-2 shrink-0 rounded-full ${color}`} aria-hidden />
          {label}
        </span>
      ))}
    </div>
  );
};

function ResourceStatusMonitor({
  activeFile,
  onRefresh,
  onGetHeaderActions,
  onTitleChange,
  onRegisterSettingsGetter,
  initialSettings,
}) {
  const { t } = useTranslation();
  const {
    loading,
    error,
    monitorData,
    resourceLastActiveDate,
    objectTypes,
    availableResources,
    isMetadataLoaded,
    // TileConfig
    title,
    selectedObjectType,
    cycleStartActivity,
    cycleEndActivity,
    configuredResources,
    shiftStart,
    shiftEnd,
    breaks,
    toleranceThreshold,
    ignoreShortTransitions,
    includeNoDataResources,
    multiResourceMode,
    // Initialization
    isFullyInitializedRef,
    // ViewState
    viewResources,
    selectedDate,
    rollingOffset,
    // TileConfig setters
    setTitle,
    setSelectedObjectType,
    setConfiguredResources,
    setShiftStart,
    setShiftEnd,
    setBreaks,
    setToleranceThreshold,
    setIgnoreShortTransitions,
    setIncludeNoDataResources,
    setMultiResourceMode,
    downtimeRedThreshold,
    setDowntimeRedThreshold,
    blockSizeMinutes,
    setBlockSizeMinutes,
    showShiftOnly,
    setShowShiftOnly,
    resourceColumn,
    tooltipObjectType,
    setTooltipObjectType,
    tooltipAttributes,
    setTooltipAttributes,
    // ViewState setters
    setViewResources,
    setSelectedDate,
    setRollingOffset,
    // Break helpers
    addBreak,
    removeBreak,
    updateBreak,
    // Actions
    fetchMetadata,
    fetchMonitorData,
  } = useResourceStatusMonitor(activeFile, initialSettings);

  const metadata = useOcelMetadata(activeFile);

  // The tooltip's object type is deliberately independent of the tile's own
  // selectedObjectType (which plays no role in the resource/downtime
  // computation, only a cache key) — empty tooltipObjectType falls back to
  // selectedObjectType for convenience, but a user can point the tooltip at
  // whichever type actually has resource-tagged events.
  const effectiveTooltipObjectType = tooltipObjectType || selectedObjectType;

  // Unit + type lookup for the extended tooltip's cell formatting, scoped
  // to effectiveTooltipObjectType (mirrors AggregationKPI.jsx).
  const attributeUnitMap = useMemo(() => {
    const map = {};
    const schemas = effectiveTooltipObjectType
      ? (metadata?.object_types_schema ?? []).filter((t) => t.name === effectiveTooltipObjectType)
      : (metadata?.object_types_schema ?? []);
    for (const t of schemas) {
      for (const attr of (t.attributes ?? [])) {
        if (attr.unit) map[attr.name] = attr.unit;
      }
    }
    return map;
  }, [metadata, effectiveTooltipObjectType]);

  const attributeTypeMap = useMemo(() => {
    const map = {};
    const schemas = effectiveTooltipObjectType
      ? (metadata?.object_types_schema ?? []).filter((t) => t.name === effectiveTooltipObjectType)
      : (metadata?.object_types_schema ?? []);
    for (const t of schemas) {
      for (const attr of (t.attributes ?? [])) {
        if (attr.type) map[attr.name] = attr.type;
      }
    }
    return map;
  }, [metadata, effectiveTooltipObjectType]);

  const formatCellValue = useCallback((attr, value) => {
    if (value == null) return "";
    const type = attributeTypeMap[attr];
    if (type === "timestamp" || type === "date") {
      return formatBerlinDisplay(value) ?? "";
    }
    const isNumericAttr = type === "float" || type === "integer" || type === "double" || type === "number";
    return formatAttributeValue(value, {
      hasDecimals: true,
      unit: attributeUnitMap[attr],
      isNumericAttr,
    });
  }, [attributeTypeMap, attributeUnitMap]);

  useEffect(() => {
    onRegisterSettingsGetter?.(() => ({
      title, selectedObjectType, cycleStartActivity, cycleEndActivity, configuredResources,
      shiftStart, shiftEnd, breaks, toleranceThreshold, ignoreShortTransitions,
      includeNoDataResources, multiResourceMode, downtimeRedThreshold, blockSizeMinutes, showShiftOnly,
      tooltipObjectType, tooltipAttributes,
      viewResources, selectedDate, rollingOffset,
    }));
  }, [title, selectedObjectType, cycleStartActivity, cycleEndActivity, // eslint-disable-line react-hooks/exhaustive-deps
      configuredResources, shiftStart, shiftEnd, breaks, toleranceThreshold, ignoreShortTransitions,
      includeNoDataResources, multiResourceMode, downtimeRedThreshold, blockSizeMinutes, showShiftOnly,
      tooltipObjectType, tooltipAttributes,
      viewResources, selectedDate, rollingOffset]);

  // Re-sync the tile title to the parent header — also fires after the
  // mount-restore effect applies a saved title.
  const onTitleChangeRef = useRef(onTitleChange);
  useEffect(() => { onTitleChangeRef.current = onTitleChange; });
  useEffect(() => {
    onTitleChangeRef.current?.(title || "Resource Status Monitor");
  }, [title]);

  const { filterState } = useGlobalFilters();
  const isFirstFilterRenderRef = useRef(true);

  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [viewResourceError, setViewResourceError] = useState("");
  const viewResourcesRef = useRef(viewResources);
  const resourceSelectionDirtyRef = useRef(false);

  useEffect(() => {
    viewResourcesRef.current = viewResources;
  }, [viewResources]);

  // Settings modal handlers

  const handleOpenSettings = useCallback(() => {
    setIsSettingsModalOpen(true);
  }, []);

  const handleApplySettings = useCallback(
    async (values) => {
      const normalizedValues = {
        ...values,
        toleranceThreshold: normalizeNonNegativeNumber(
          values.toleranceThreshold,
          0,
        ),
        downtimeRedThreshold: normalizeNonNegativeNumber(
          values.downtimeRedThreshold,
          0,
        ),
        blockSizeMinutes: [5, 10, 15, 20].includes(values.blockSizeMinutes)
          ? values.blockSizeMinutes
          : 15,
      };
      const nextViewResources =
        normalizedValues.configuredResources.length > 0
          ? (() => {
              const kept = viewResources.filter((r) =>
                normalizedValues.configuredResources.includes(r),
              );
              return kept.length > 0
                ? kept
                : normalizedValues.configuredResources;
            })()
          : [];

      if (onTitleChange) onTitleChange(normalizedValues.title);

      setTitle(normalizedValues.title);
      setSelectedObjectType(normalizedValues.selectedObjectType);
      setConfiguredResources(normalizedValues.configuredResources);
      setShiftStart(normalizedValues.shiftStart);
      setShiftEnd(normalizedValues.shiftEnd);
      setBreaks(normalizedValues.breaks);
      setToleranceThreshold(normalizedValues.toleranceThreshold);
      setIgnoreShortTransitions(normalizedValues.ignoreShortTransitions);
      setIncludeNoDataResources(normalizedValues.includeNoDataResources);
      setMultiResourceMode(normalizedValues.multiResourceMode);
      setDowntimeRedThreshold(normalizedValues.downtimeRedThreshold);
      setBlockSizeMinutes(normalizedValues.blockSizeMinutes);
      setShowShiftOnly(normalizedValues.showShiftOnly);
      setTooltipObjectType(normalizedValues.tooltipObjectType ?? "");
      setTooltipAttributes(normalizedValues.tooltipAttributes ?? []);
      setViewResources(nextViewResources);
      setSelectedDate(normalizedValues.selectedDate);
      setRollingOffset(normalizedValues.rollingOffset);

      await fetchMonitorData({
        ...normalizedValues,
        viewResources: nextViewResources,
      });
    },
    [
      fetchMonitorData,
      onTitleChange,
      viewResources,
      setBreaks,
      setConfiguredResources,
      setIgnoreShortTransitions,
      setIncludeNoDataResources,
      setMultiResourceMode,
      setDowntimeRedThreshold,
      setBlockSizeMinutes,
      setShowShiftOnly,
      setTooltipObjectType,
      setTooltipAttributes,
      setSelectedDate,
      setRollingOffset,
      setSelectedObjectType,
      setShiftEnd,
      setShiftStart,
      setTitle,
      setToleranceThreshold,
      setViewResources,
    ],
  );

  // Re-fetch when global filters change

  useEffect(() => {
    if (isFirstFilterRenderRef.current) {
      isFirstFilterRenderRef.current = false;
      return;
    }
    if (!isFullyInitializedRef.current) return;
    fetchMetadata();
    if (
      configuredResources.length > 0 &&
      selectedObjectType &&
      viewResources.length > 0
    ) {
      fetchMonitorData();
    }
  }, [filterState]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-load & refresh

  useAutoLoad(fetchMetadata, [activeFile], isMetadataLoaded);

  useEffect(() => {
    if (onRefresh) {
      onRefresh(async () => {
        if (isMetadataLoaded) await fetchMonitorData();
      });
    }
  }, [onRefresh, isMetadataLoaded, fetchMonitorData]);

  // Header

  const headerActions = useMemo(
    () => (
      <button
        onClick={handleOpenSettings}
        className="flex items-center gap-1 px-2 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
        aria-label={t('common:actions.openSettings')}
      >
        <svg
          className="w-3 h-3"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
        <span className="text-xs">{t('common:actions.settings')}</span>
      </button>
    ),
    [handleOpenSettings],
  );

  useEffect(() => {
    if (onGetHeaderActions) onGetHeaderActions(headerActions);
  }, [onGetHeaderActions, headerActions]);

  // View Mode handlers

  const triggerAutoApply = useCallback(
    (overrides = {}) => {
      const effectiveViewResources =
        overrides.viewResources ?? viewResourcesRef.current;

      if (!effectiveViewResources || effectiveViewResources.length === 0) {
        setViewResourceError("Select at least one resource.");
        return false;
      }
      setViewResourceError("");

      fetchMonitorData({
        viewResources: effectiveViewResources,
        ...overrides,
      });
      return true;
    },
    [fetchMonitorData],
  );

  const handleViewResourcesChange = useCallback(
    (nextResources) => {
      resourceSelectionDirtyRef.current = true;
      viewResourcesRef.current = nextResources;
      setViewResources(nextResources);
      setViewResourceError(
        nextResources.length > 0 ? "" : "Select at least one resource.",
      );
    },
    [setViewResources],
  );

  const handleResourceDropdownOpenChange = useCallback(
    (isOpen) => {
      if (isOpen || !resourceSelectionDirtyRef.current) return;
      resourceSelectionDirtyRef.current = false;
      triggerAutoApply({ viewResources: viewResourcesRef.current });
    },
    [triggerAutoApply],
  );

  const handleSelectedDateCommit = useCallback(
    (nextDate) => {
      setSelectedDate(nextDate);
      setRollingOffset(null);
      triggerAutoApply({ selectedDate: nextDate, rollingOffset: null });
    },
    [setSelectedDate, setRollingOffset, triggerAutoApply],
  );

  // Derived state

  const showLoadDataPrompt = !isMetadataLoaded && !loading && !error;
  const showConfigPrompt =
    isMetadataLoaded && !loading && !error && configuredResources.length === 0;
  const showContent =
    isMetadataLoaded && !error && configuredResources.length > 0;
  const resourceDataItems = monitorData?.resource_data || [];
  const showMonitor = showContent && resourceDataItems.length > 0;
  const showNoData =
    showContent &&
    !loading &&
    monitorData !== null &&
    resourceDataItems.length === 0;
  const showSelectionPrompt = showContent && !loading && monitorData === null;

  // Auto-fetch monitor data once configuration is complete (from either the
  // per-file localStorage restore or the centralized dashboard config) and
  // metadata has loaded — mirrors ResourceUtilizationChart's auto-apply effect.
  // Also re-fetches whenever selectedDate advances past what was last
  // fetched (not just on the initial load) — otherwise a rolling date preset
  // (e.g. "Heute") rolling over to a new day only updates the displayed date
  // label, leaving monitorData stale until a manual refresh. fetchMonitorData
  // itself de-dupes same-date/in-flight calls, so this can't cause redundant
  // network calls for the flows that already call it directly (manual date
  // pick, Settings apply, filter changes, refresh button).
  const lastFetchedDateRef = useRef(null);
  useEffect(() => {
    if (!showContent || loading || error) return;
    if (viewResources.length === 0 || !selectedDate) return;
    if (monitorData !== null && lastFetchedDateRef.current === selectedDate) return;
    lastFetchedDateRef.current = selectedDate;
    fetchMonitorData();
  }, [showContent, loading, error, monitorData, viewResources, selectedDate, fetchMonitorData]);

  return (
    <BaseTile title={title} loading={loading} minHeight="lg" className="h-full">
      <TileContent layout="flex" direction="column" gap="md" grow>
        {showLoadDataPrompt && (
          <TileStatusContent
            status="info"
            title="Load Resources Data"
            message="Load metadata first to configure available resources."
            action={
              <button
                onClick={fetchMetadata}
                className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600"
              >
                Load Resources Data
              </button>
            }
          />
        )}

        {showConfigPrompt && (
          <TileStatusContent
            status="empty"
            title="Configure Status Monitor"
            message="Open settings to configure resources, shift, and breaks."
            action={
              <button
                onClick={handleOpenSettings}
                className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600"
              >
                Configure Monitor
              </button>
            }
          />
        )}

        {showContent && (
          <>
            {/* View Mode Controls */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 no-drag">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                <div className="sm:w-96">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Resources
                  </label>
                  <MultiSelectDropdown
                    options={configuredResources}
                    selectedValues={viewResources}
                    onSelectionChange={handleViewResourcesChange}
                    onOpenChange={handleResourceDropdownOpenChange}
                    placeholder="Select resources..."
                    itemType="resources"
                    className="text-sm w-full"
                    minOptionWidth="0"
                    explicitDisplayCount={5}
                  />
                  {viewResourceError && (
                    <p className="mt-1 text-xs text-error-600">
                      {viewResourceError}
                    </p>
                  )}
                </div>
                {showMonitor && (
                  <div className="pb-0.5">
                    <StatusLegend redThresholdMinutes={downtimeRedThreshold} showShiftOnly={showShiftOnly} />
                  </div>
                )}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Date
                  </label>
                  <div className="flex items-center gap-2">
                    <DateInput
                      value={selectedDate}
                      onChange={handleSelectedDateCommit}
                      disabled={loading}
                    />
                    <span className="text-xs text-gray-500">
                      {formatDisplayDate(selectedDate)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {showMonitor && (
              <div className="flex-1 min-h-0 flex flex-col">
                <TooltipProvider>
                  <div className="divide-y divide-gray-200 border border-gray-200 rounded-lg bg-white px-4 overflow-y-auto custom-scrollbar flex-1 min-h-0">
                    {resourceDataItems.map((rd) => (
                      <ResourceTracker
                        key={rd.resource}
                        resource={rd.resource}
                        slots={rd.slots}
                        multiResourceMode={monitorData.multi_resource_mode}
                        downtimeRedThreshold={downtimeRedThreshold}
                        showShiftOnly={showShiftOnly}
                        shiftStart={shiftStart}
                        shiftEnd={shiftEnd}
                        activeFile={activeFile}
                        resourceColumn={resourceColumn}
                        objectType={effectiveTooltipObjectType}
                        selectedDate={selectedDate}
                        tooltipAttributes={tooltipAttributes}
                        formatCellValue={formatCellValue}
                      />
                    ))}
                  </div>
                </TooltipProvider>
                <div className="flex items-center justify-between px-4 mt-1">
                  <span className="text-xs text-gray-500">{showShiftOnly ? shiftStart : "00:00"}</span>
                  <span className="text-xs text-gray-500">{showShiftOnly ? shiftEnd : "24:00"}</span>
                </div>
              </div>
            )}

            {showNoData && (
              <TileStatusContent
                status="empty"
                title="No Data Available"
                message="No status data found for the selected resources and date."
              />
            )}

            {showSelectionPrompt && (
              <TileStatusContent
                status="empty"
                title="Select Filters to Load Monitor"
                message="Select resources and a date. Data loads automatically when resources dropdown closes."
              />
            )}
          </>
        )}

        {error && (
          <TileStatusContent
            status="error"
            title="Failed to load monitor"
            message={typeof error === "string" ? error : "Unexpected error."}
            action={
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    if (isMetadataLoaded) fetchMonitorData();
                    else fetchMetadata();
                  }}
                  className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600"
                >
                  Retry
                </button>
                <button
                  onClick={handleOpenSettings}
                  className="px-4 py-2 bg-gray-200 text-gray-800 text-sm font-medium rounded-md hover:bg-gray-300"
                >
                  Open Settings
                </button>
              </div>
            }
          />
        )}
      </TileContent>

      {/* Settings Modal */}
      <TileSettingsModal
        open={isSettingsModalOpen}
        onOpenChange={setIsSettingsModalOpen}
        title="Resource Status Monitor Settings"
        getInitialValues={() => ({
          title,
          selectedObjectType,
          configuredResources: [...configuredResources],
          shiftStart,
          shiftEnd,
          breaks: breaks.map((b) => ({ ...b })),
          toleranceThreshold,
          ignoreShortTransitions,
          includeNoDataResources,
          multiResourceMode,
          downtimeRedThreshold,
          blockSizeMinutes,
          showShiftOnly,
          tooltipObjectType,
          tooltipAttributes: [...tooltipAttributes],
          selectedDate,
          rollingOffset,
        })}
        onApply={handleApplySettings}
        isApplyDisabled={(values) =>
          !(
            isMetadataLoaded &&
            values.selectedObjectType &&
            values.configuredResources?.length > 0 &&
            values.selectedDate
          ) || loading
        }
        applyButtonText={loading ? "Calculating..." : "Apply and Calculate"}
      >
        {({ values, setField, update }) => (
          <ResourceStatusMonitorSettings
            title={values.title}
            objectTypes={objectTypes}
            selectedObjectType={values.selectedObjectType}
            availableResources={availableResources}
            configuredResources={values.configuredResources}
            shiftStart={values.shiftStart}
            shiftEnd={values.shiftEnd}
            breaks={values.breaks}
            toleranceThreshold={values.toleranceThreshold}
            ignoreShortTransitions={values.ignoreShortTransitions}
            includeNoDataResources={values.includeNoDataResources}
            multiResourceMode={values.multiResourceMode}
            onTitleChange={(v) => setField("title", v)}
            onObjectTypeChange={(v) => setField("selectedObjectType", v)}
            onConfiguredResourcesChange={(v) =>
              setField("configuredResources", v)
            }
            onShiftStartChange={(v) => setField("shiftStart", v)}
            onShiftEndChange={(v) => setField("shiftEnd", v)}
            onAddBreak={() =>
              update((p) => ({
                ...p,
                breaks: [...p.breaks, { start: "12:00", end: "12:30" }],
              }))
            }
            onRemoveBreak={(idx) =>
              update((p) => ({
                ...p,
                breaks: p.breaks.filter((_, i) => i !== idx),
              }))
            }
            onUpdateBreak={(idx, field, val) =>
              update((p) => ({
                ...p,
                breaks: p.breaks.map((b, i) =>
                  i === idx ? { ...b, [field]: val } : b,
                ),
              }))
            }
            onToleranceThresholdChange={(v) =>
              setField("toleranceThreshold", v)
            }
            onIgnoreShortTransitionsChange={(v) =>
              setField("ignoreShortTransitions", v)
            }
            onIncludeNoDataResourcesChange={(v) =>
              setField("includeNoDataResources", v)
            }
            onMultiResourceModeChange={(v) => setField("multiResourceMode", v)}
            downtimeRedThreshold={values.downtimeRedThreshold}
            onDowntimeRedThresholdChange={(v) =>
              setField("downtimeRedThreshold", v)
            }
            blockSizeMinutes={values.blockSizeMinutes}
            onBlockSizeMinutesChange={(v) => setField("blockSizeMinutes", v)}
            showShiftOnly={values.showShiftOnly}
            onShowShiftOnlyChange={(v) => setField("showShiftOnly", v)}
            tooltipObjectType={values.tooltipObjectType ?? ""}
            onTooltipObjectTypeChange={(v) => setField("tooltipObjectType", v)}
            tooltipAttributes={values.tooltipAttributes ?? []}
            onTooltipAttributesChange={(v) => setField("tooltipAttributes", v)}
            activeFile={activeFile}
            selectedDate={values.selectedDate}
            rollingOffset={values.rollingOffset}
            resourceLastActiveDate={resourceLastActiveDate}
            onSelectedDateChange={(dateStr, offset) =>
              update((p) => ({ ...p, selectedDate: dateStr, rollingOffset: offset }))
            }
            loading={loading}
          />
        )}
      </TileSettingsModal>
    </BaseTile>
  );
}

export default ResourceStatusMonitor;
